import { Check, Zap, Server, Shield } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { sileo } from 'sileo';
import { Header } from '../../widgets/header';
import { Button, Card, CardHeader, CardContent, Badge } from '../../shared/ui';
import { useUpgradeToPro, useCurrentUser } from '../../shared/api';

export function PricingPage() {
    const navigate = useNavigate();
    const { data: user } = useCurrentUser();
    const upgradeTierMutation = useUpgradeToPro();

    const [isUpgrading, setIsUpgrading] = useState(false);

    const handleUpgradeToPro = async () => {
        if (!user) {
            sileo.warning({
                title: 'Authentication Required',
                description: 'Please login or register to upgrade.',
            });
            navigate({ to: '/login' });
            return;
        }

        if (user.tier === 'pro' || user.tier === 'enterprise') {
            sileo.info({
                title: 'Already Subscribed',
                description: `You are already on the ${user.tier} plan!`,
            });
            return;
        }

        setIsUpgrading(true);
        sileo.info({
            title: 'Processing Checkout',
            description: 'Simulating successful secure payment...',
        });

        // Simulate network/payment delay for dramatic effect
        setTimeout(async () => {
            try {
                await upgradeTierMutation.mutateAsync();
                sileo.success({
                    title: 'Payment Successful',
                    description: 'Welcome to DevLint Pro! Deep ML Analysis is now unlocked.',
                });
                navigate({ to: '/dashboard' });
            } catch (err) {
                sileo.error({
                    title: 'Payment Failed',
                    description: 'There was an issue processing the simulation.',
                });
            } finally {
                setIsUpgrading(false);
            }
        }, 2000);
    };

    const tiers = [
        {
            name: 'Free',
            description: 'Essential security for individual developers.',
            price: '$0',
            icon: Shield,
            features: [
                'Local pre-commit SAST scanning',
                'Basic dependency vulnerability checks',
                'VS Code extension features',
                'Up to 1 linked project',
                'Community support',
            ],
            buttonText: 'Get Started',
            variant: 'outline' as const,
        },
        {
            name: 'Pro',
            description: 'Advanced ML protection and cloud analytics for professionals.',
            price: '$15',
            period: '/month',
            icon: Zap,
            features: [
                'Everything in Free',
                'Full Machine Learning analysis',
                'Cloud Dashboard metrics',
                'Unlimited linked projects',
                'Deep dependency scanning',
                'Priority email support',
            ],
            buttonText: 'Upgrade to Pro',
            variant: 'primary' as const,
            popular: true,
        },
        {
            name: 'Enterprise',
            description: 'Complete security control and audit capabilities for teams.',
            price: 'Custom',
            icon: Server,
            features: [
                'Everything in Pro',
                'Custom SAST rules',
                'On-premise deployment options',
                'SAML Single Sign-On (SSO)',
                'Advanced audit logging & compliance',
                'Dedicated success manager',
            ],
            buttonText: 'Contact Sales',
            variant: 'outline' as const,
        },
    ];

    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            {/* Decorative gradient background elements */}
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px] -z-10 mix-blend-screen" />
            <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] -z-10 mix-blend-screen" />

            <Header />

            <main className="container mx-auto px-8 flex flex-col items-center justify-center pt-32 pb-24">
                {/* Improved Hero Section */}
                <div className="text-center max-w-4xl mx-auto mb-20 space-y-8 relative z-10 px-4">
                    <div className="inline-block animate-fade-in-up">
                        <Badge severity="info" className="mb-6 px-4 py-1.5 text-sm uppercase tracking-widest border border-primary/20 shadow-[0_0_15px_rgba(var(--primary),0.2)]">
                            Simple, transparent pricing
                        </Badge>
                    </div>

                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-text-primary tracking-tight font-mono leading-tight mb-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
                        Secure your code,{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary-hover to-primary animate-pulse-glow">
                            accelerate your shipping.
                        </span>
                    </h1>

                    <p className="text-lg md:text-2xl text-text-secondary font-mono max-w-3xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '200ms' }}>
                        Choose the perfect plan for your project size. Upgrade anytime to access ML-powered deep analysis.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto w-full">
                    {tiers.map((tier) => {
                        const Icon = tier.icon;
                        return (
                            <div key={tier.name} className={`relative ${tier.popular ? '-mt-4 mb-4' : ''}`}>
                                {tier.popular && (
                                    <div className="absolute -top-4 left-0 right-0 flex justify-center z-10">
                                        <span className="bg-primary text-text-inverse text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider shadow-glow-primary">
                                            Most Popular
                                        </span>
                                    </div>
                                )}
                                <Card
                                    padding="lg"
                                    corners="asymmetric"
                                    className={`h-full flex flex-col ${tier.popular ? 'border-primary/50 shadow-glow-primary bg-surface/80 backdrop-blur-xl' : 'hover:border-primary/30 transition-colors'}`}
                                >
                                    <CardHeader className="text-center pb-2">
                                        <div className="h-12 w-12 mx-auto rounded-xl bg-surface-hover flex items-center justify-center mb-4">
                                            <Icon className={`h-6 w-6 ${tier.popular ? 'text-primary' : 'text-text-secondary'}`} />
                                        </div>
                                        <h2 className="text-2xl font-bold font-mono text-text-primary">{tier.name}</h2>
                                        <p className="text-sm text-text-muted mt-2 font-mono h-10">{tier.description}</p>
                                    </CardHeader>

                                    <CardContent className="flex-1 flex flex-col">
                                        <div className="text-center my-8">
                                            <span className="text-5xl font-bold text-text-primary font-mono">{tier.price}</span>
                                            {tier.period && (
                                                <span className="text-text-secondary font-mono ml-1">{tier.period}</span>
                                            )}
                                        </div>

                                        <ul className="space-y-4 mb-8 flex-1">
                                            {tier.features.map((feature, i) => (
                                                <li key={i} className="flex items-start gap-3">
                                                    <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                                                    <span className="text-sm text-text-secondary font-mono">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <Button
                                            variant={tier.variant}
                                            corners="asymmetric"
                                            className="w-full mt-auto"
                                            onClick={tier.name === 'Pro' ? handleUpgradeToPro : undefined}
                                            disabled={tier.name === 'Pro' && isUpgrading}
                                        >
                                            {tier.name === 'Pro' && isUpgrading ? 'Processing...' : tier.buttonText}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </div>
                        );
                    })}
                </div>
            </main>
        </div>
    );
}
